import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import useUserDetail from 'src/components/userAdmin/UserAdminDetailContext';
import { USER_DETAIL_PAGE_CARD_TEST_ID } from 'src/components/userAdmin/UserDetailPage/index';

import PermissionCard from '../PermissionCard';

import { styles } from 'src/components/userAdmin/UserDetailPage/components/UserSurveySettings/index.styles';

export const USER_DETAIL_PAGE_ROLE_DROPDOWN_TEST_ID = 'user-detail-page-role';
export const USER_SETTINGS_SURVEY_ACCESS_DROPDOWN_TEST_ID =
  'user-settings-survey-access';
export const USER_SURVEY_SETTINGS_ACCORDION_TEST_ID =
  'user-survey-settings-accordion';
export const USER_ADMIN_ACTIVATE_SURVEY_ACCESS_TEST_ID =
  'user-survey-settings-activate-survey-access';
export const USER_ADMIN_DEACTIVATE_SURVEY_ACCESS_TEST_ID =
  'user-survey-settings-deactivate-survey-access';

interface UserSettingsProps {}

const UserSurveySettings: FC<UserSettingsProps> = () => {
  const { t } = useTranslation('admin');
  const {
    fieldErrors,
    handleRoleChange,
    handleFieldChange,
    user,
    canEditRoles,
    modulePermissions,
  } = useUserDetail();

  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();

  return (
    <Box sx={styles.accordion}>
      <Accordion
        data-test-id={USER_SURVEY_SETTINGS_ACCORDION_TEST_ID}
        elevation={0}
        defaultExpanded
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{
          borderRadius: '0 0 30px 30px solid',
        }}
      >
        <AccordionSummary sx={styles.accordionSummary}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant={'h5'} sx={{ p: '0.5rem' }}>
              {t('Detail.surveyRelatedUserSettings')}
            </Typography>
            <ExpandLessIcon
              sx={[
                {
                  ...styles.accordionExpansionIcon(theme),
                  transition: 'transform 0.3s',
                },
                expanded
                  ? {
                      transform: 'rotate(0deg)',
                    }
                  : {
                      transform: 'rotate(180deg)',
                    },
              ]}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={styles.accordionDetails}>
          <Stack direction="row" sx={{ gap: 0.75, ...styles.inputs }}>
            {/* Role */}
            <Box
              component="label"
              sx={fieldErrors.role ? styles.fieldError : { pl: 0 }}
              id="admin-role-setting"
            >
              <Typography sx={{ fontWeight: 600, pb: '0.5rem' }}>
                {t('admin:Detail.role')}
              </Typography>
              <Select
                sx={(theme) => ({ ...styles.dropdown(theme), width: '18rem' })}
                className="user-admin-detail-select-wrapper-select"
                disabled={!canEditRoles.length}
                name="role"
                aria-labelledby="admin-role-setting"
                onChange={handleRoleChange}
                value={user.role && canEditRoles.length ? user.role.id : ''}
                data-test-id={USER_DETAIL_PAGE_ROLE_DROPDOWN_TEST_ID}
                variant="outlined"
                displayEmpty
                renderValue={(selected) =>
                  !selected ? (
                    <Box sx={styles.placeholder}>{t('admin:Detail.role')}</Box>
                  ) : (
                    canEditRoles.find(({ id }) => id === selected)?.displayName
                  )
                }
              >
                {canEditRoles.map((roleOption, i) => (
                  <MenuItem value={roleOption.id} key={`role-option-${i}`}>
                    {roleOption.displayName}
                  </MenuItem>
                ))}
                {fieldErrors.role && (
                  <ErrorMessage align="left">{fieldErrors.role}</ErrorMessage>
                )}
              </Select>
            </Box>

            {/* Survey Access */}
            <Box
              data-test-id={USER_SETTINGS_SURVEY_ACCESS_DROPDOWN_TEST_ID}
              component="label"
              sx={
                fieldErrors.is_user_active_in_survey
                  ? styles.fieldError
                  : { flex: 1 }
              }
              id="admin-label-setting"
            >
              <Typography sx={{ fontWeight: 600, pb: '0.5rem' }}>
                {t('admin:Detail.surveyActivationStatus')}
              </Typography>
              <Select
                // @ts-expect-error TS2783
                sx={(theme) => ({ width: '40rem', ...styles.dropdown(theme) })}
                className="user-admin-detail-select-wrapper-select"
                name="is_user_active_in_survey"
                aria-labelledby="admin-label-setting"
                onChange={handleFieldChange}
                value={
                  user.is_user_active_in_survey === null
                    ? ''
                    : String(user.is_user_active_in_survey)
                }
                variant="outlined"
                displayEmpty
                renderValue={(selected) =>
                  selected === '' ? (
                    <Box sx={styles.placeholder}>
                      {t('admin:Detail.surveyActivationStatus')}
                    </Box>
                  ) : selected === 'true' ? (
                    t('admin:Detail.active')
                  ) : (
                    t('admin:Detail.inactive')
                  )
                }
              >
                <MenuItem
                  value="true"
                  data-test-id={USER_ADMIN_ACTIVATE_SURVEY_ACCESS_TEST_ID}
                >
                  {t('admin:Detail.active')}
                </MenuItem>
                <MenuItem
                  value="false"
                  data-test-id={USER_ADMIN_DEACTIVATE_SURVEY_ACCESS_TEST_ID}
                >
                  {t('admin:Detail.inactive')}
                </MenuItem>
              </Select>
              {fieldErrors.is_user_active_in_survey && (
                <ErrorMessage align="left">
                  {fieldErrors.is_user_active_in_survey}
                </ErrorMessage>
              )}
            </Box>
          </Stack>
          <Divider orientation="horizontal" sx={styles.divider} />
          <Typography sx={{ fontWeight: 600, pt: '1rem', pl: '1rem' }}>
            {t('Detail.moduleAccessPermissions')}
          </Typography>
          {/*Module Access Permissions*/}
          <Box sx={styles.permissionsContainer}>
            {modulePermissions.map((modulePermission) => (
              <Box
                sx={styles.permissionsBox}
                key={modulePermission.module.id}
                data-test-id={USER_DETAIL_PAGE_CARD_TEST_ID}
              >
                <PermissionCard modulePermission={modulePermission} />
              </Box>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default UserSurveySettings;
